import React from 'react';
import { useLanguage } from './i18n/LanguageContext';

function Team() {
  const { t } = useLanguage();
  const teamMembers = [
    {
      name: t.team.pengTao.name,
      title: t.team.pengTao.title,
      bio: t.team.pengTao.bio,
      education: t.team.pengTao.education
    },
    {
      name: t.team.kane.name,
      title: t.team.kane.title,
      bio: t.team.kane.bio,
      education: t.team.kane.education
    }
  ];

  return (
    <div className="page-content">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img src="/dubai-aerial.jpg" alt="Team" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-dark/70 via-navy-primary/60 to-blue-accent/30"></div>

        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-accent/20 rounded-full blur-3xl animate-pulse"></div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-8 animate-fade-in-up">
            <span className="inline-block px-4 py-2 mb-6 text-sm font-medium tracking-widest uppercase bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              {t.team.heroTag}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-4 tracking-tight">
              {t.team.heroTitle}
            </h1>
            <p className="text-xl font-light opacity-90 max-w-2xl mx-auto">
              {t.team.heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Team Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <p className="text-lg leading-relaxed text-gray-600 mb-6">
            {t.team.introPara1}
          </p>
          <p className="text-lg leading-relaxed text-gray-600">
            {t.team.introPara2}
          </p>
        </div>
      </section>

      {/* Team Members */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="space-y-12">
            {teamMembers.map((member, idx) => (
              <div
                key={idx}
                className="bg-white overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 group"
              >
                <div className="md:flex">
                  {/* Left side - Name and Title */}
                  <div className="md:w-1/3 bg-gradient-to-br from-navy-primary to-navy-light p-8 md:p-12 flex flex-col justify-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-blue-accent/20 rounded-full blur-3xl"></div>
                    <div className="relative">
                      <div className="w-20 h-20 bg-white/10 flex items-center justify-center mb-6">
                        <svg className="w-10 h-10 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <h2 className="text-3xl font-medium text-white mb-2">{member.name}</h2>
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-0.5 bg-gold-accent"></span>
                        <span className="text-light-blue font-light text-lg">{member.title}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Bio */}
                  <div className="md:w-2/3 p-8 md:p-12">
                    <div className="space-y-4">
                      {member.bio.map((para, i) => (
                        <p key={i} className="text-gray-600 leading-relaxed">{para}</p>
                      ))}
                    </div>
                    <div className="mt-8 pt-6 border-t border-gray-100">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-blue-accent mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                        </svg>
                        <div>
                          <span className="font-semibold text-navy-primary text-sm">{t.team.education}</span>
                          <p className="text-gray-600 text-sm mt-1">{member.education}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-accent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-gold-accent rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-4">{t.team.ctaTitle}</h2>
          <p className="text-white/70 text-lg mb-8">
            {t.team.ctaSubtitle}
          </p>
          <a
            href="/career"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-accent to-light-blue text-white font-medium hover:shadow-2xl hover:shadow-blue-accent/30 transition-all duration-300 hover:-translate-y-1"
          >
            {t.team.viewCareers}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </section>
    </div>
  );
}

export default Team;
